import { Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { Settings, LogOut, Building2, ChevronDown, Crown, Menu, Briefcase } from "lucide-react";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { trackRouteView, trackTimeOnRoute } from "@/lib/app-analytics";
import { useAuth } from "@/lib/auth";
import { useEffectiveNav } from "@/hooks/useEffectiveNav";
import { useLongPress } from "@/hooks/useLongPress";
import { openNavCustomizer } from "@/lib/nav-customizer";
import { buzz } from "@/lib/dar-prefs";
import type { NavItem } from "@/lib/nav-catalog";

import { Logo } from "./Logo";
import { useBranding } from "@/lib/branding";
import { AppBackgroundBrand } from "./AppBackgroundBrand";
import { NotificationsBell } from "./NotificationsBell";
import { MobileBottomNav } from "./MobileBottomNav";
import { RadioAutoListener } from "./messages/RadioAutoListener";

import { OfflineStatusChip } from "./OfflineStatusChip";
import { DashboardOriginBar } from "@/components/DashboardOriginBar";
import { useNavTrail } from "@/lib/nav-trail";
import { SosBanner } from "./SosBanner";
import { SosButton } from "./SosButton";
import { SideMenu } from "./SideMenu";

// Overlay layers that never form the first paint are split out of the startup
// bundle and mounted once the app is interactive. Behaviour is unchanged.
const AIHelperBubble = lazy(() =>
  import("./AIHelperBubble").then((m) => ({ default: m.AIHelperBubble })),
);
const MrVoice = lazy(() =>
  import("@/components/voice/MrVoice").then((m) => ({ default: m.MrVoice })),
);
const DarActionOrb = lazy(() =>
  import("./dar/DarActionOrb").then((m) => ({ default: m.DarActionOrb })),
);
const InstallAppBanner = lazy(() =>
  import("./InstallAppBanner").then((m) => ({ default: m.InstallAppBanner })),
);
const CustomizeNavDialog = lazy(() =>
  import("@/components/nav/CustomizeNavDialog").then((m) => ({ default: m.CustomizeNavDialog })),
);
const LocationConsentHost = lazy(() =>
  import("@/lib/location-consent").then((m) => ({ default: m.LocationConsentHost })),
);
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function AppShell() {
  const { user, currentOrg, currentRole, memberships, setCurrentOrgId, signOut, isPlatformAdmin } =
    useAuth();
  // Records visited paths so child screens can offer "Back to <parent>".
  useNavTrail();
  const branding = useBranding();

  const isClient = currentRole === "client_viewer";
  const nav = useEffectiveNav();
  const clientPortalNav: NavItem = {
    id: "portal",
    route: "/portal",
    label: "Client Portal",
    icon: Briefcase,
    permission: "client_portal.view",
    placement: "sidebar",
  };
  const visibleNav = (isClient ? [clientPortalNav] : nav.sidebarItems).filter(
    (item) => item.id !== "settings",
  );

  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  // Mount the floating overlays after the first screen is interactive.
  const [overlaysReady, setOverlaysReady] = useState(false);
  useEffect(() => {
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void) => number })
      .requestIdleCallback;
    if (idle) {
      const id = idle(() => setOverlaysReady(true));
      return () => (window as unknown as { cancelIdleCallback?: (i: number) => void })
        .cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(() => setOverlaysReady(true), 200);
    return () => window.clearTimeout(t);
  }, []);
  // Long-press the hamburger = shortcut into Customize Navigation (never a drag target).
  const hamburgerPress = useLongPress(() => {
    buzz(18);
    setMenuOpen(false);
    openNavCustomizer();
  });

  // Clients are redirected away from staff routes to their portal.
  useEffect(() => {
    if (
      isClient &&
      !location.pathname.startsWith("/portal") &&
      !location.pathname.startsWith("/settings") &&
      !location.pathname.startsWith("/onboarding")
    ) {
      navigate({ to: "/portal", replace: true });
    }
  }, [isClient, location.pathname, navigate]);

  // Track route views + time-on-route for product analytics.
  const routeStartRef = useRef<{ path: string; at: number }>({
    path: location.pathname,
    at: Date.now(),
  });
  useEffect(() => {
    const ctx = { userId: user?.id ?? null, orgId: currentOrg?.id ?? null };
    // Flush previous route's duration before recording the new view.
    const prev = routeStartRef.current;
    if (prev.path && prev.path !== location.pathname) {
      trackTimeOnRoute(prev.path, Date.now() - prev.at, ctx);
    }
    routeStartRef.current = { path: location.pathname, at: Date.now() };
    trackRouteView(location.pathname, ctx);
  }, [location.pathname, user?.id, currentOrg?.id]);

  // Flush time-on-route on tab close / pagehide.
  useEffect(() => {
    const flush = () => {
      const { path, at } = routeStartRef.current;
      trackTimeOnRoute(path, Date.now() - at, {
        userId: user?.id ?? null,
        orgId: currentOrg?.id ?? null,
      });
    };
    window.addEventListener("pagehide", flush);
    return () => window.removeEventListener("pagehide", flush);
  }, [user?.id, currentOrg?.id]);

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: "/" });
  };

  const initials = (user?.email ?? "U").slice(0, 2).toUpperCase();

  return (
    <RadioAutoListener>
    <div className="relative flex min-h-dvh w-full max-w-full overflow-x-hidden bg-background">
      <AppBackgroundBrand opacity={0.05} blur={1} animate position="center" />
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden h-dvh w-64 flex-col overflow-hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground md:flex">
        <div className="flex h-16 items-center border-b border-sidebar-border px-5">
          {isClient ? (
            <div className="flex items-center gap-2">
              {branding.logo_url ? (
                <img
                  src={branding.logo_url}
                  alt={currentOrg?.name ?? "Org"}
                  className="h-9 w-9 rounded object-contain"
                />
              ) : (
                <Building2 className="h-7 w-7 text-primary" />
              )}
              <span className="font-display text-sm font-bold uppercase tracking-wider truncate">
                {currentOrg?.name ?? "Client Portal"}
              </span>
            </div>
          ) : (
            <Logo />
          )}
        </div>

        {/* Org switcher */}
        <div className="px-3 pt-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex w-full items-center gap-2 rounded-md border border-sidebar-border bg-sidebar-accent px-3 py-2 text-left text-sm hover:bg-sidebar-accent/80">
                <Building2 className="h-4 w-4 text-primary" />
                <span className="flex-1 truncate font-medium">{currentOrg?.name ?? "No org"}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
              {memberships.map((m) => (
                <DropdownMenuItem
                  key={m.organization_id}
                  onClick={() => setCurrentOrgId(m.organization_id)}
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  <span className="flex-1 truncate">{m.organizations.name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{m.role}</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate({ to: "/onboarding" })}>
                + New organization
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain px-3 py-4" data-dock-target="sidebar">
          {isPlatformAdmin && (
            <Link
              to="/admin"
              className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                location.pathname.startsWith("/admin")
                  ? "bg-primary/15 text-primary font-semibold"
                  : "text-foreground hover:bg-primary/10"
              }`}
            >
              <Crown className="h-4 w-4" />
              Platform Admin
            </Link>
          )}
          {visibleNav.map((item) => {
            const active = location.pathname.startsWith(item.route);
            const Icon = item.icon;
            return (
              <Link
                key={item.id}
                to={item.route as never}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  active
                    ? "bg-sidebar-primary/15 text-sidebar-foreground font-semibold"
                    : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground"
                }`}
              >
                <Icon className={`h-4 w-4 ${active ? "text-sidebar-primary" : ""}`} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="shrink-0 border-t border-sidebar-border p-3">
          <Link
            to="/settings"
            className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
              location.pathname.startsWith("/settings")
                ? "bg-sidebar-primary/15 font-semibold text-sidebar-foreground"
                : "text-sidebar-foreground/80 hover:bg-sidebar-accent"
            }`}
          >
            <Settings
              className={`h-4 w-4 ${location.pathname.startsWith("/settings") ? "text-sidebar-primary" : ""}`}
            />
            Settings
          </Link>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col md:ml-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur md:px-6">
          <div className="flex items-center gap-2">
            <button
              type="button"
              {...hamburgerPress.handlers}
              onClick={(e) => {
                hamburgerPress.handlers.onClick(e);
                if (e.defaultPrevented) return;
                setMenuOpen(true);
              }}
              aria-label="Open menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="md:hidden">
              <Logo />
            </div>
          </div>
          <div className="flex flex-1 items-center justify-end gap-3">
            <OfflineStatusChip className="hidden sm:flex" />
            {currentOrg?.plan === "trial" && (
              <span className="hidden rounded-full border border-warning/40 bg-warning/10 px-3 py-1 text-xs font-medium text-warning sm:inline">
                Free Trial
              </span>
            )}
            <div className="hidden sm:block">
              <SosButton compact />
            </div>
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary">
                  {initials}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate({ to: "/settings" })}>
                  <Settings className="mr-2 h-4 w-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <SosBanner />
        <main className="min-w-0 flex-1 overflow-x-hidden pb-20 md:pb-0">
          {/* Contextual return action when the user arrived from a dashboard */}
          <div className="px-4 pt-3 md:px-6 empty:hidden">
            <DashboardOriginBar className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" />
          </div>
          <Outlet />
        </main>
        {/* Mobile-only top-of-content offline chip; auto-hides when healthy */}
        <div className="fixed left-3 top-3 z-30 sm:hidden">
          <OfflineStatusChip />
        </div>
        {/* Floating mobile SOS removed — pages render an inline SOS control instead */}

        {overlaysReady ? (
          <Suspense fallback={null}>
            <DarActionOrb />
            <AIHelperBubble />
            {/* Optional "Hey MR" conversational layer; renders nothing when disabled. */}
            <MrVoice />
            <InstallAppBanner />
          </Suspense>
        ) : null}
        <MobileBottomNav />
      </div>
      <SideMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
      {overlaysReady ? (
        <Suspense fallback={null}>
          <CustomizeNavDialog />
          <LocationConsentHost orgId={currentOrg?.id ?? null} />
        </Suspense>
      ) : null}
    </div>
    </RadioAutoListener>
  );
}
